import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
 
@Component({
  selector: 'app-date-picker-reusable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './date-picker-reusable.html',
})
export class DatePickerReusableComponent implements OnInit, OnDestroy {
  private static readonly overlayGap = 8;
  private static readonly overlayMinHeight = 320;
  private static readonly defaultMaxYear = 2050;
 
  private valueToIso(value: unknown): string {
    if (value == null) return '';
    return this.normalizeToIsoDate(value) ?? '';
  }
 
  @Input() control?: FormControl | AbstractControl | null;
  @Input() startControl?: FormControl | AbstractControl | null;
  @Input() endControl?: FormControl | AbstractControl | null;
  @Input() mode: 'single' | 'range' = 'single';
  @Input() size: 'sm' | 'md' | 'lg' = 'lg';
  @Input() placeholder = 'dd-mm-yyyy';
  @Input() maxDate?: string;
  @Input() disableFutureDates = false;
 
  @Output() valueChange = new EventEmitter<any>();
 
  protected isOpen = false;
  protected openAbove = false;
  protected overlayStyle: Record<string, string> = {};
  protected leftMonth = this.startOfMonth(this.getDefaultCalendarDate());
  protected rightMonth = this.addMonths(this.leftMonth, 1);
  protected weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  protected monthOptions = [
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 },
  ];
  protected yearOptions: number[] = [];
  protected currentYear = new Date().getFullYear();
 
  protected singleFocused = false;
  protected startFocused = false;
  protected endFocused = false;
  protected rawSingle = '';
  protected rawStart = '';
  protected rawEnd = '';
  protected pendingIso = '';
  protected pendingStartIso = '';
  protected pendingEndIso = '';
  private blurTimer: ReturnType<typeof setTimeout> | undefined;
 
  @ViewChild('triggerButton') private triggerButton?: ElementRef<HTMLElement>;
 
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
 
  private readonly scrollListener = (): void => {
    if (this.isOpen) {
      this.updateOverlayPosition();
    }
  };
 
  private readonly outsideClickListener = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Node) || !this.hostElement.nativeElement.contains(target)) {
      this.closeOverlay();
      this.cdr.detectChanges();
    }
  };
 
  private buildYearOptions(): number[] {
    const currentYear = this.currentYear;
    const minYear = currentYear - 100;
    const maxYear = this.getMaxSelectableDate().getFullYear();
    const years: number[] = [];
    for (let year = maxYear; year >= minYear; year--) {
      years.push(year);
    }
    return years;
  }
 
  private getOpeningMonth(): Date {
    if (this.disableFutureDates) {
      return this.startOfMonth(new Date());
    }
    if (this.maxDate) {
      const maxParsed = this.parseIsoDate(this.maxDate);
      if (maxParsed) return maxParsed;
    }
    return this.getDefaultCalendarDate();
  }
 
  ngOnInit(): void {
    this.yearOptions = this.buildYearOptions();
    if (this.mode === 'single' && this.control) {
      const parsed = this.parseIsoDate(this.control.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      if (this.control.value) {
        this.rawSingle = this.formatDisplay(this.control.value);
      }
    }
    if (this.mode === 'range' && this.startControl) {
      const parsed = this.parseIsoDate(this.startControl.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      if (this.startControl.value) {
        this.rawStart = this.formatDisplay(this.startControl.value);
      }
      if (this.endControl?.value) {
        this.rawEnd = this.formatDisplay(this.endControl.value);
      }
    }
  }
 
  ngOnDestroy(): void {
    clearTimeout(this.blurTimer);
    this.closeOverlay();
  }
 
  private openCalendar(): void {
    this.overlayStyle = {
      position: 'fixed',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '99999',
    };
    this.isOpen = true;
    this.cdr.detectChanges();
    this.updateOverlayPosition();
    this.bindGlobalListeners();
  }
 
  protected toggle(): void {
    if (this.control?.disabled || this.startControl?.disabled) return;
    if (this.isOpen) {
      this.closeOverlay();
      return;
    }
    if (this.mode === 'single') {
      const parsed = this.parseIsoDate(this.control?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
    } else {
      const parsed = this.parseIsoDate(this.startControl?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
    }
    this.openCalendar();
  }
 
  protected onSingleFocus(): void {
    if (this.control?.disabled) return;
    clearTimeout(this.blurTimer);
    this.singleFocused = true;
    this.rawSingle = this.control?.value ? this.formatDisplay(this.control.value) : '';
    if (!this.isOpen) {
      const parsed = this.parseIsoDate(this.control?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.openCalendar();
    }
  }
 
  protected onSingleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.formatAsDateMask(input.value);
    input.value = masked;
    this.rawSingle = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingIso = iso ?? '';
    if (iso) {
      this.leftMonth = this.startOfMonth(this.parseIsoDate(iso)!);
    }
    this.cdr.detectChanges();
  }
 
  protected onSingleBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.singleFocused = false;
      this.pendingIso = '';
      const iso = this.parseTypedDate(value);
      if (iso) {
        this.control?.setValue(iso);
        this.control?.markAsTouched();
        this.valueChange.emit(iso);
      } else if (!value.trim()) {
        this.control?.setValue('');
        this.valueChange.emit('');
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  protected onStartFocus(): void {
    if (this.startControl?.disabled) return;
    clearTimeout(this.blurTimer);
    this.startFocused = true;
    this.rawStart = this.startControl?.value ? this.formatDisplay(this.startControl.value) : '';
    if (!this.isOpen) {
      const parsed = this.parseIsoDate(this.startControl?.value);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      this.openCalendar();
    }
  }
 
  protected onStartInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.formatAsDateMask(input.value);
    input.value = masked;
    this.rawStart = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingStartIso = iso ?? '';
    if (iso) {
      this.leftMonth = this.startOfMonth(this.parseIsoDate(iso)!);
    }
    this.cdr.detectChanges();
  }
 
  protected onStartBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.startFocused = false;
      this.pendingStartIso = '';
      const iso = this.parseTypedDate(value);
      if (iso) {
        this.startControl?.setValue(iso);
        this.startControl?.markAsTouched();
        const endIso = this.normalizeToIsoDate(this.endControl?.value) ?? '';
        if (endIso && iso > endIso) {
          this.endControl?.setValue('');
          this.rawEnd = '';
        }
        this.valueChange.emit({ start: iso, end: this.endControl?.value || '' });
      } else if (!value.trim()) {
        this.startControl?.setValue('');
        this.endControl?.setValue('');
        this.rawEnd = '';
        this.valueChange.emit({ start: '', end: '' });
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  protected onEndFocus(): void {
    if (this.startControl?.disabled) return;
    clearTimeout(this.blurTimer);
    this.endFocused = true;
    this.rawEnd = this.endControl?.value ? this.formatDisplay(this.endControl.value) : '';
    if (!this.isOpen) {
      const ref = this.endControl?.value ?? this.startControl?.value;
      const parsed = this.parseIsoDate(ref);
      this.leftMonth = this.startOfMonth(parsed ?? this.getOpeningMonth());
      this.rightMonth = this.addMonths(this.leftMonth, 1);
      this.openCalendar();
    }
  }
 
  protected onEndInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.formatAsDateMask(input.value);
    input.value = masked;
    this.rawEnd = masked;
    const iso = this.parseTypedDate(masked);
    this.pendingEndIso = iso ?? '';
    if (iso) {
      const endDate = this.parseIsoDate(iso)!;
      this.rightMonth = this.startOfMonth(endDate);
      if (this.leftMonth >= this.rightMonth) {
        this.leftMonth = this.addMonths(this.rightMonth, -1);
      }
    }
    this.cdr.detectChanges();
  }
 
  protected onEndBlur(value: string): void {
    this.blurTimer = setTimeout(() => {
      this.endFocused = false;
      this.pendingEndIso = '';
      const iso = this.parseTypedDate(value);
      const startIso = this.normalizeToIsoDate(this.startControl?.value) ?? '';
      if (iso && (!startIso || iso >= startIso)) {
        this.endControl?.setValue(iso);
        this.endControl?.markAsTouched();
        this.valueChange.emit({ start: startIso, end: iso });
      } else if (!value.trim()) {
        this.endControl?.setValue('');
        this.valueChange.emit({ start: startIso, end: '' });
      } else {
        this.rawEnd = this.endControl?.value ? this.formatDisplay(this.endControl.value) : '';
      }
      this.cdr.detectChanges();
    }, 200);
  }
 
  private formatAsDateMask(value: string): string {
    const d = this.constrainDateDigits(value.replace(/\D/g, ''));
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
  }
 
  private constrainDateDigits(digits: string): string {
    let result = '';
    let pos = 0;
 
    // Day first digit
    if (pos >= digits.length) return result;
    const d1 = Number(digits[pos]);
    if (d1 > 3) {
      result += '0' + d1; pos++;  // "7" → "07"
    } else {
      result += digits[pos++];
      if (pos < digits.length) {
        const d2 = Number(digits[pos]);
        const day = d1 * 10 + d2;
        if (day === 0) { result += '1'; pos++; }        // 00 → 01
        else if (day <= 31) { result += digits[pos++]; } // valid
        else { pos++; }                                   // >31: skip
      }
    }
 
    if (result.length < 2) return result;
 
    // Month first digit
    if (pos >= digits.length) return result;
    const m1 = Number(digits[pos]);
    if (m1 > 1) {
      result += '0' + m1; pos++;  // "9" → "09"
    } else {
      result += digits[pos++];
      if (pos < digits.length) {
        const m2 = Number(digits[pos]);
        const month = m1 * 10 + m2;
        if (month === 0) { result += '1'; pos++; }        // 00 → 01
        else if (month <= 12) { result += digits[pos++]; } // valid
        else { pos++; }                                     // >12: skip
      }
    }
 
    if (result.length < 4) return result;
 
 // Year: any 4 digits
    while (pos < digits.length && result.length < 8) {
      result += digits[pos++];
    }
 
    return result;
  }
 
  private parseTypedDate(text: string): string | null {
    const trimmed = text.trim();
    if (!trimmed) return null;
 
    const dmyMatch = trimmed.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      const date = this.buildValidatedDate(Number(year), Number(month), Number(day));
      if (date) {
        const iso = this.toIsoDate(date);
        return this.isDateDisabled(iso) ? null : iso;
      }
    }
 
    const isoMatch = trimmed.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      const date = this.buildValidatedDate(Number(year), Number(month), Number(day));
      if (date) {
        const iso = this.toIsoDate(date);
        return this.isDateDisabled(iso) ? null : iso;
      }
    }
 
    return null;
  }
 
  private getDefaultCalendarDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }
 
  protected get todayIso(): string {
    return this.toIsoDate(new Date());
  }
 
  protected clearRange(event?: Event): void {
    event?.stopPropagation();
    if (this.mode === 'range' && this.startControl && this.endControl) {
      this.startControl.setValue('');
      this.endControl.setValue('');
      this.rawStart = '';
      this.rawEnd = '';
      this.pendingStartIso = '';
      this.pendingEndIso = '';
      this.valueChange.emit({ start: '', end: '' });
    }
  }
 
  protected isDateDisabled(iso: string): boolean {
    return iso > this.getMaxSelectableIsoDate();
  }
 
  protected isNextMonthDisabled(month: Date): boolean {
    const next = this.addMonths(month, 1);
    const nextIso = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-01`;
    return nextIso > this.getMaxSelectableIsoDate();
  }
 
  protected selectDate(iso: string): void {
    if (this.isDateDisabled(iso)) return;
 
    if (this.mode === 'single' && this.control) {
      clearTimeout(this.blurTimer);
      this.singleFocused = false;
      this.pendingIso = '';
      this.control.setValue(iso);
      this.control.markAsTouched();
      this.rawSingle = this.formatDisplay(iso);
      this.valueChange.emit(iso);
      this.closeOverlay();
      return;
    }
 
    if (this.mode === 'range' && this.startControl && this.endControl) {
      const start = String(this.startControl.value || '');
      const end = String(this.endControl.value || '');
 
      if (!start && !end) {
        clearTimeout(this.blurTimer);
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.rawStart = this.formatDisplay(iso);
        this.rawEnd = '';
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }
 
      if (start && !end && iso < start) {
        clearTimeout(this.blurTimer);
        this.startControl.setValue(iso);
        this.endControl.setValue('');
        this.rawStart = this.formatDisplay(iso);
        this.rawEnd = '';
        this.valueChange.emit({ start: iso, end: '' });
        return;
      }
 
      if (start && !end && iso >= start) {
        clearTimeout(this.blurTimer);
        this.endControl.setValue(iso);
        this.rawEnd = this.formatDisplay(iso);
        this.valueChange.emit({ start, end: iso });
        this.closeOverlay();
        return;
      }
 
      if (start && end) {
        if (iso < start) {
          clearTimeout(this.blurTimer);
          this.startControl.setValue(iso);
          this.endControl.setValue('');
          this.rawStart = this.formatDisplay(iso);
          this.rawEnd = '';
          this.valueChange.emit({ start: iso, end: '' });
        } else {
          clearTimeout(this.blurTimer);
          this.endControl.setValue(iso);
          this.rawEnd = this.formatDisplay(iso);
          this.valueChange.emit({ start, end: iso });
          this.closeOverlay();
        }
        return;
      }
 
      clearTimeout(this.blurTimer);
      this.startControl.setValue(iso);
      this.endControl.setValue('');
      this.rawStart = this.formatDisplay(iso);
      this.rawEnd = '';
      this.valueChange.emit({ start: iso, end: '' });
    }
  }
 
  protected isSelected(iso: string): boolean {
    if (this.mode === 'single') {
      if (this.singleFocused && this.pendingIso) return this.pendingIso === iso;
      return this.normalizeToIsoDate(this.control?.value) === iso;
    }
    const start = (this.startFocused && this.pendingStartIso)
      ? this.pendingStartIso
      : (this.normalizeToIsoDate(this.startControl?.value) || '');
    const end = (this.endFocused && this.pendingEndIso)
      ? this.pendingEndIso
      : (this.normalizeToIsoDate(this.endControl?.value) || '');
    return iso === start || iso === end;
  }
 
  protected isInRange(iso: string): boolean {
    if (this.mode === 'single') return false;
    const start = (this.startFocused && this.pendingStartIso)
      ? this.pendingStartIso
      : (this.normalizeToIsoDate(this.startControl?.value) || '');
    const end = (this.endFocused && this.pendingEndIso)
      ? this.pendingEndIso
      : (this.normalizeToIsoDate(this.endControl?.value) || '');
    if (!start || !end) return false;
    return iso > start && iso < end;
  }
 
  protected getCalendarDays(
    monthDate: Date,
  ): Array<{ iso: string; day: number; inCurrentMonth: boolean }> {
    const start = this.startOfMonth(monthDate);
    const firstWeekday = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - firstWeekday);
 
    const days: Array<{ iso: string; day: number; inCurrentMonth: boolean }> = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);
      days.push({
        iso: this.toIsoDate(date),
        day: date.getDate(),
        inCurrentMonth:
          date.getMonth() === monthDate.getMonth() &&
          date.getFullYear() === monthDate.getFullYear(),
      });
    }
    return days;
  }
 
  protected formatDisplay(value: unknown): string {
    const iso = this.valueToIso(value);
    if (!iso) return this.placeholder;
    const parsed = this.parseIsoDate(iso);
    if (!parsed) return this.placeholder;
    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }
 
  protected prevMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, -1);
    else this.rightMonth = this.addMonths(this.rightMonth, -1);
  }
 
  protected nextMonth(which: 'left' | 'right'): void {
    if (which === 'left') this.leftMonth = this.addMonths(this.leftMonth, 1);
    else this.rightMonth = this.addMonths(this.rightMonth, 1);
  }
 
  protected onMonthChange(which: 'left' | 'right', value: string): void {
    const month = Number(value);
    if (Number.isNaN(month) || month < 0 || month > 11) return;
    if (which === 'left') this.leftMonth = new Date(this.leftMonth.getFullYear(), month, 1);
    else this.rightMonth = new Date(this.rightMonth.getFullYear(), month, 1);
  }
 
  protected onYearChange(which: 'left' | 'right', value: string): void {
    const year = Number(value);
    if (Number.isNaN(year)) return;
    if (which === 'left') this.leftMonth = new Date(year, this.leftMonth.getMonth(), 1);
    else this.rightMonth = new Date(year, this.rightMonth.getMonth(), 1);
  }
 
  private updateOverlayPosition(): void {
    if (!this.triggerButton) return;
    const rect = this.triggerButton.nativeElement.getBoundingClientRect();
    const gap = DatePickerReusableComponent.overlayGap;
    const dropdownHeight = DatePickerReusableComponent.overlayMinHeight;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const calendarWidth = Math.min(320, window.innerWidth - 32);
    this.openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    if (this.openAbove) {
      this.overlayStyle = {
        position: 'fixed',
        left: `${Math.floor(rect.left)}px`,
        width: `${Math.floor(calendarWidth)}px`,
        bottom: `${Math.floor(window.innerHeight - rect.top + gap)}px`,
        top: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    } else {
      this.overlayStyle = {
        position: 'fixed',
        left: `${Math.floor(rect.left)}px`,
        width: `${Math.floor(calendarWidth)}px`,
        top: `${Math.floor(rect.bottom + gap)}px`,
        bottom: 'auto',
        zIndex: '99999',
        opacity: '1',
        pointerEvents: 'auto',
      };
    }
    this.cdr.detectChanges();
  }
 
  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }
 
  private addMonths(date: Date, delta: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + delta, 1);
  }
 
  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
 
  private getMaxSelectableDate(): Date {
    const candidates: Date[] = [];
    if (this.disableFutureDates) {
      candidates.push(new Date());
    }
    const parsedMaxDate = this.parseIsoDate(this.maxDate);
    if (parsedMaxDate) {
      candidates.push(parsedMaxDate);
    }
    if (candidates.length === 0) {
      return new Date(DatePickerReusableComponent.defaultMaxYear, 11, 31);
    }
    return candidates.reduce((earliest, current) =>
      current.getTime() < earliest.getTime() ? current : earliest,
    );
  }
 
  private getMaxSelectableIsoDate(): string {
    return this.toIsoDate(this.getMaxSelectableDate());
  }
 
  private closeOverlay(): void {
    this.isOpen = false;
    this.unbindGlobalListeners();
  }
 
  private bindGlobalListeners(): void {
    document.addEventListener('scroll', this.scrollListener, { capture: true, passive: true });
    window.addEventListener('resize', this.scrollListener, { passive: true });
    document.addEventListener('pointerdown', this.outsideClickListener, true);
  }
 
  private unbindGlobalListeners(): void {
    document.removeEventListener('scroll', this.scrollListener, true);
    window.removeEventListener('resize', this.scrollListener);
    document.removeEventListener('pointerdown', this.outsideClickListener, true);
  }
 
  private parseIsoDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    const isoMatch = raw.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:T.*)?$/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }
    const dmyMatch = raw.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      return this.buildValidatedDate(Number(year), Number(month), Number(day));
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
 
  private buildValidatedDate(year: number, month: number, day: number): Date | null {
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }
 
  private normalizeToIsoDate(value: unknown): string | null {
    const parsed = this.parseIsoDate(value);
    if (!parsed) return null;
    return this.toIsoDate(parsed);
  }
}
 
 